import React, { useState, useRef, useEffect } from 'react'
import { Typography, Chip, TextField, Collapse, Select, MenuItem, FormControlLabel, Switch, Slider } from '@material-ui/core'
import { ExpandMore, ExpandLess, TuneRounded } from '@material-ui/icons'
import axios from 'axios'
import AreaBreakdownTable from './AreaBreakdownTable'
import ComponentRecipeSummary from './ComponentRecipeSummary'

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })

const plainObject = (value) => {
  if (!value) return {}
  if (value instanceof Map) return Object.fromEntries(value.entries())
  return typeof value === 'object' ? value : {}
}

const parseInputValue = (value) => {
  const raw = String(value ?? '').trim()
  const num = Number(raw)
  return raw !== '' && Number.isFinite(num) ? num : raw
}

const RecipeInputControls = ({ projectService, url, onUpdate }) => {
  const [template, setTemplate] = useState(null)
  const [values, setValues] = useState({})
  const [savingKey, setSavingKey] = useState('')
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    axios.get(`${url}/api/bid-estimation/service-templates`, { params: { serviceId: projectService.serviceId } })
      .then(res => {
        const templates = res.data || []
        const tmpl = templates.find(t => t.serviceId === projectService.serviceId)
        setTemplate(tmpl || null)
        const nextValues = { ...plainObject(projectService.inputValues) }
        const inputSlots = tmpl?.inputSlots || []
        inputSlots.forEach(slot => {
          if (!slot?.key) return
          if ((nextValues[slot.key] === undefined || nextValues[slot.key] === null || nextValues[slot.key] === '') && slot.defaultValue != null) {
            nextValues[slot.key] = slot.defaultValue
          }
        })
        setValues(nextValues)
      })
      .catch(() => {})
  }, [projectService.serviceId, projectService._id])

  const inputSlots = (template?.inputSlots || []).filter(slot => slot.key && slot.key !== 'assembly_qty' && slot.key !== 'assembly_unit')
  if (!template || inputSlots.length === 0) return null

  const saveInput = async (key, rawValue) => {
    const nextInputValues = {
      ...plainObject(projectService.inputValues),
      ...values,
      [key]: parseInputValue(rawValue)
    }
    setSavingKey(key)
    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, { inputValues: nextInputValues })
      onUpdate()
    } catch (e) {
      console.error('Recipe input update failed:', e)
    } finally {
      setSavingKey('')
    }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 16 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          <TuneRounded fontSize="small" style={{ color: '#2e7d32' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Recipe Inputs
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {inputSlots.length} variable{inputSlots.length !== 1 ? 's' : ''}
          </Typography>
          {savingKey && <Typography variant="caption" style={{ color: '#f57c00' }}>Saving...</Typography>}
        </div>
      </div>
      <Collapse in={expanded}>
        <div style={{ padding: '4px 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {inputSlots.map(slot => {
            const value = values[slot.key] ?? ''
            const required = !(slot.tags || []).includes('optional') && slot.defaultValue == null
            const missing = required && String(value).trim() === ''
            return (
              <TextField
                key={slot.key}
                size="small"
                variant="outlined"
                label={slot.label || slot.key}
                value={value}
                error={missing}
                helperText={missing ? 'Required' : (slot.unit || slot.key)}
                onChange={(e) => setValues(prev => ({ ...prev, [slot.key]: e.target.value }))}
                onBlur={(e) => saveInput(slot.key, e.target.value)}
                InputProps={{ style: { fontSize: 13, width: 150 } }}
                InputLabelProps={{ style: { fontSize: 12 } }}
              />
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

const VariationControls = ({ projectService, url, onUpdate }) => {
  const [template, setTemplate] = useState(null)
  const [selections, setSelections] = useState({})
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    // Load the service template to get variation definitions
    axios.get(`${url}/api/bid-estimation/service-templates`, { params: { serviceId: projectService.serviceId } })
      .then(res => {
        const templates = res.data || []
        const tmpl = templates.find(t => t.serviceId === projectService.serviceId)
        if (tmpl && tmpl.variations && tmpl.variations.length > 0) {
          setTemplate(tmpl)
          // Initialize from saved appliedVariations or template defaults
          const saved = projectService.appliedVariations || {}
          const initial = {}
          for (const v of tmpl.variations) {
            initial[v.attributeId] = saved[v.attributeId] !== undefined ? saved[v.attributeId] : v.defaultValue
          }
          setSelections(initial)
        }
      })
      .catch(() => {})
  }, [projectService.serviceId, projectService._id])

  const handleChange = async (attributeId, value) => {
    const next = { ...selections, [attributeId]: value }
    setSelections(next)
    setSaving(true)
    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}/variations`, { variations: next })
      onUpdate()
    } catch (e) {
      console.error('Variation update failed:', e)
    } finally {
      setSaving(false)
    }
  }

  if (!template || !template.variations || template.variations.length === 0) return null

  return (
    <div style={{ background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 16 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          <TuneRounded fontSize="small" style={{ color: '#1565c0' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Recipe Variations
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {template.variations.length} option{template.variations.length !== 1 ? 's' : ''}
          </Typography>
          {saving && <Typography variant="caption" style={{ color: '#f57c00' }}>Saving...</Typography>}
        </div>
      </div>
      <Collapse in={expanded}>
        <div style={{ padding: '4px 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {template.variations.map(v => {
            const val = selections[v.attributeId]
            if (v.type === 'boolean') {
              return (
                <FormControlLabel
                  key={v.attributeId}
                  control={<Switch size="small" checked={!!val} onChange={(e) => handleChange(v.attributeId, e.target.checked)} color="primary" />}
                  label={<span style={{ fontSize: 13 }}>{v.label}</span>}
                />
              )
            }
            if (v.type === 'range') {
              return (
                <div key={v.attributeId} style={{ minWidth: 180 }}>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>{v.label}: <b>{val}{v.unit || ''}</b></Typography>
                  <Slider
                    size="small" min={v.min} max={v.max} step={v.step} value={val || v.min}
                    onChange={(e, newVal) => handleChange(v.attributeId, newVal)}
                    style={{ marginTop: -4 }}
                  />
                </div>
              )
            }
            // select
            return (
              <div key={v.attributeId}>
                <Typography variant="caption" color="textSecondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>{v.label}</Typography>
                <Select
                  size="small" variant="outlined" value={val || ''} onChange={(e) => handleChange(v.attributeId, e.target.value)}
                  style={{ fontSize: 13, minWidth: 140, height: 32 }}
                >
                  {(v.options || []).map(opt => (
                    <MenuItem key={opt.value} value={opt.value} style={{ fontSize: 13 }}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </div>
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

const ServiceDetail = ({ projectService, project, url, onUpdate }) => {
  const [areasExpanded, setAreasExpanded] = useState(false)
  const debounceRef = useRef(null)

  const hasAreas = (projectService.areas || []).length > 0
  const breakdownLabel = projectService.source === 'scope' ? 'Scope Items / Quantities' : 'Area Breakdown'
  const areaColumnLabel = projectService.source === 'scope' ? 'Source Item' : 'Area'
  const breakdownCountLabel = projectService.source === 'scope' ? 'source item' : 'area'

  // Direct quantity/unit editing — works without areas
  // Auto-triggers recalculate when quantity changes so costs update immediately
  const handleFieldChange = (field, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, { [field]: value })
        onUpdate()
      } catch (e) {
        console.error('Service update failed:', e)
      }
    }, 600)
  }

  return (
    <div>
      {/* Service header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {projectService.serviceCategory}
          </div>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {projectService.serviceName}
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Direct qty/unit input — always visible */}
          <TextField
            size="small" variant="outlined" type="number" placeholder="Qty"
            defaultValue={projectService.totalQuantity || ''}
            onBlur={(e) => {
              const val = Number(e.target.value)
              if (val !== projectService.totalQuantity) {
                handleFieldChange('totalQuantity', val)
              }
            }}
            InputProps={{ style: { fontSize: 13, width: 80 } }}
          />
          <TextField
            size="small" variant="outlined" placeholder="Unit"
            defaultValue={projectService.unit || ''}
            onBlur={(e) => {
              if (e.target.value !== projectService.unit) {
                handleFieldChange('unit', e.target.value)
              }
            }}
            InputProps={{ style: { fontSize: 13, width: 60 } }}
          />
          <Chip size="small" label={`Cost: ${fmt(projectService.totalCost)}`}
            style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
          <Chip size="small" label={`Sell: ${fmt(projectService.totalSell)}`}
            style={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
        </div>
      </div>

      {/* Variation controls — only shown if template has variations */}
      <VariationControls projectService={projectService} url={url} onUpdate={onUpdate} />

      <RecipeInputControls projectService={projectService} url={url} onUpdate={onUpdate} />

      {/* Collapsible area breakdown — optional */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 20 }}>
        <div
          onClick={() => setAreasExpanded(!areasExpanded)}
          style={{
            padding: '8px 16px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', cursor: 'pointer', userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {areasExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {breakdownLabel}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {hasAreas ? `${projectService.areas.length} ${breakdownCountLabel}${projectService.areas.length !== 1 ? 's' : ''}` : 'Optional — click to add'}
            </Typography>
          </div>
        </div>
        <Collapse in={areasExpanded}>
          <AreaBreakdownTable projectService={projectService} url={url} onUpdate={onUpdate} areaColumnLabel={areaColumnLabel} />
        </Collapse>
      </div>

      <ComponentRecipeSummary projectService={projectService} project={project} url={url} onUpdate={onUpdate} />
    </div>
  )
}

export default ServiceDetail
